import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  isProfileValid,
  MAX_SNAPSHOT_SCALE,
  MAX_SNAPSHOT_SIZE,
  MAX_SNAPSHOT_SIZES,
  MIN_SNAPSHOT_SIZE,
  MIN_SNAPSHOT_SIZES,
  objectsPerAvatar,
  rasterWidth,
  sizesToValues,
  SNAPSHOT_FORMATS,
  toggleScale,
  validateSnapshotProfileDraft,
  valuesToSizes,
} from '../../shared/snapshot-profile-form'
import type {
  SnapshotFormatName,
  SnapshotProfileDraft,
} from '../../shared/snapshot-profile-form'
import type { SnapshotProfileInput } from '../../shared/types'

export interface SnapshotProfileEditorProps {
  sizes: number[]
  scales: number[]
  formats: string[]
  jpegBackground: string
  isDefault: boolean
  saving: boolean
  apiError?: string | null
  onSave: (input: SnapshotProfileInput) => void
}

/** Every density the API accepts, so the row is data rather than four copied buttons. */
const DENSITIES = Array.from({ length: MAX_SNAPSHOT_SCALE }, (_, i) => i + 1)

export default function SnapshotProfileEditor({
  sizes,
  scales,
  formats,
  jpegBackground,
  isDefault,
  saving,
  apiError,
  onSave,
}: SnapshotProfileEditorProps) {
  const { formatMessage } = useIntl()
  const [draft, setDraft] = useState<SnapshotProfileDraft>(() => ({
    sizes: sizesToValues(sizes),
    scales: [...scales],
    formats: formats as SnapshotFormatName[],
    jpegBackground,
  }))

  const errors = useMemo(() => validateSnapshotProfileDraft(draft), [draft])
  const valid = isProfileValid(errors)
  const saved = `${sizes.join('|')}/${scales.join('|')}/${formats.join('|')}/${jpegBackground}`
  const current = `${valuesToSizes(draft.sizes).join('|')}/${draft.scales.join('|')}/${draft.formats.join('|')}/${draft.jpegBackground}`
  const dirty = saved !== current
  const maxScale = draft.scales.length ? Math.max(...draft.scales) : 1

  const sizeError = (i: number) => {
    const code = errors.sizes[i]
    if (code === undefined) {
      return undefined
    }

    return formatMessage(
      { id: `avatarStudio.imageSizes.errors.${code}` },
      {
        min: MIN_SNAPSHOT_SIZE,
        max: MAX_SNAPSHOT_SIZE,
        previous: draft.sizes[i - 1],
        scale: maxScale,
        raster: rasterWidth(Number(draft.sizes[i]), maxScale),
      },
    )
  }

  const patch = (next: Partial<SnapshotProfileDraft>) =>
    setDraft((prev) => ({ ...prev, ...next }))

  return (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      <Alert severity="info">
        {formatMessage({ id: 'avatarStudio.imageSizes.effectNote' })}
      </Alert>

      {isDefault && !dirty && (
        <Alert severity="info">
          {formatMessage({ id: 'avatarStudio.imageSizes.defaultNote' })}
        </Alert>
      )}

      {apiError && <Alert severity="error">{apiError}</Alert>}

      {/* --- sizes ------------------------------------------------------------------------- */}
      <Typography variant="subtitle2">
        {formatMessage({ id: 'avatarStudio.imageSizes.sizesTitle' })}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatMessage({ id: 'avatarStudio.imageSizes.sizesHelp' })}
      </Typography>

      <Stack spacing={1.25}>
        {draft.sizes.map((value, i) => (
          <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
            <TextField
              size="small"
              type="number"
              fullWidth={true}
              label={formatMessage({ id: 'avatarStudio.imageSizes.cssPixels' })}
              value={value}
              disabled={saving}
              inputProps={{
                min: MIN_SNAPSHOT_SIZE,
                max: MAX_SNAPSHOT_SIZE,
                step: 1,
                'aria-label': formatMessage(
                  { id: 'avatarStudio.imageSizes.sizeAria' },
                  { index: i + 1 },
                ),
              }}
              error={Boolean(errors.sizes[i])}
              helperText={sizeError(i)}
              // tslint:disable-next-line: jsx-no-lambda
              onChange={(e) =>
                patch({
                  sizes: draft.sizes.map((v, j) => (j === i ? e.target.value : v)),
                })
              }
            />
            {/* What is actually encoded, so nobody has to do the arithmetic in their head. */}
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexShrink: 0 }}>
              {draft.scales.map((scale) => (
                <Chip
                  key={scale}
                  size="small"
                  variant="outlined"
                  label={`${rasterWidth(Number(value) || 0, scale)}px`}
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Stack>
            {draft.sizes.length > MIN_SNAPSHOT_SIZES ? (
              <Tooltip title={formatMessage({ id: 'avatarStudio.imageSizes.removeSize' })}>
                <IconButton
                  aria-label={formatMessage(
                    { id: 'avatarStudio.imageSizes.removeSizeAria' },
                    { size: value },
                  )}
                  size="small"
                  disabled={saving}
                  sx={{ mt: 0.5 }}
                  // tslint:disable-next-line: jsx-no-lambda
                  onClick={() => patch({ sizes: draft.sizes.filter((_, j) => j !== i) })}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ))}
      </Stack>

      <Button
        size="small"
        startIcon={<AddOutlinedIcon />}
        disabled={saving || draft.sizes.length >= MAX_SNAPSHOT_SIZES}
        // tslint:disable-next-line: jsx-no-lambda
        onClick={() => patch({ sizes: [...draft.sizes, ''] })}
      >
        {formatMessage({ id: 'avatarStudio.imageSizes.addSize' })}
      </Button>

      <Divider />

      {/* --- densities --------------------------------------------------------------------- */}
      <Typography variant="subtitle2">
        {formatMessage({ id: 'avatarStudio.imageSizes.densitiesTitle' })}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatMessage({ id: 'avatarStudio.imageSizes.densitiesHelp' })}
      </Typography>
      <Stack direction="row" spacing={1}>
        {DENSITIES.map((scale) => (
          <FormControlLabel
            key={scale}
            control={
              <Switch
                size="small"
                checked={draft.scales.includes(scale)}
                // 1x can never come off: it is what a plain `src` points at, and the API refuses
                // a list without it. Disabling beats letting someone build a rejected profile.
                disabled={saving || scale === 1}
                inputProps={{
                  'aria-label': formatMessage(
                    { id: 'avatarStudio.imageSizes.densityAria' },
                    { scale },
                  ),
                }}
                // tslint:disable-next-line: jsx-no-lambda
                onChange={() => patch({ scales: toggleScale(draft.scales, scale) })}
              />
            }
            label={`${scale}x`}
          />
        ))}
      </Stack>

      <Divider />

      {/* --- formats ----------------------------------------------------------------------- */}
      <Typography variant="subtitle2">
        {formatMessage({ id: 'avatarStudio.imageSizes.formatsTitle' })}
      </Typography>
      <ToggleButtonGroup
        size="small"
        value={draft.formats}
        disabled={saving}
        aria-label={formatMessage({ id: 'avatarStudio.imageSizes.formatsTitle' })}
        // tslint:disable-next-line: jsx-no-lambda
        onChange={(_e, next: SnapshotFormatName[]) => patch({ formats: next })}
      >
        {SNAPSHOT_FORMATS.map((format) => (
          <ToggleButton key={format} value={format} aria-label={format}>
            {format}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {errors.formats && (
        <Typography variant="caption" color="error">
          {formatMessage({ id: 'avatarStudio.imageSizes.errors.noFormats' })}
        </Typography>
      )}

      {draft.formats.includes('jpeg') && (
        <TextField
          size="small"
          sx={{ maxWidth: 220 }}
          label={formatMessage({ id: 'avatarStudio.imageSizes.jpegBackground' })}
          value={draft.jpegBackground}
          disabled={saving}
          error={Boolean(errors.jpegBackground)}
          helperText={
            errors.jpegBackground
              ? formatMessage({ id: 'avatarStudio.imageSizes.errors.hex' })
              : formatMessage({ id: 'avatarStudio.imageSizes.jpegBackgroundHelp' })
          }
          // tslint:disable-next-line: jsx-no-lambda
          onChange={(e) => patch({ jpegBackground: e.target.value })}
        />
      )}

      <Divider />

      {/* The bill, in the unit that matters: objects per avatar in the bucket. */}
      <Typography variant="caption" color="text.secondary" data-testid="avatar-image-sizes-cost">
        {formatMessage(
          { id: 'avatarStudio.imageSizes.costNote' },
          { objects: objectsPerAvatar(draft) },
        )}
      </Typography>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          disabled={saving || !dirty}
          // tslint:disable-next-line: jsx-no-lambda
          onClick={() =>
            setDraft({
              sizes: sizesToValues(sizes),
              scales: [...scales],
              formats: formats as SnapshotFormatName[],
              jpegBackground,
            })
          }
        >
          {formatMessage({ id: 'avatarStudio.imageSizes.reset' })}
        </Button>
        <Button
          variant="contained"
          disabled={saving || !valid || !dirty}
          // tslint:disable-next-line: jsx-no-lambda
          onClick={() =>
            onSave({
              sizes: valuesToSizes(draft.sizes),
              scales: draft.scales,
              formats: draft.formats,
              jpegBackground: draft.jpegBackground.replace(/^#/, '').toUpperCase(),
            })
          }
        >
          {formatMessage({
            id: saving
              ? 'avatarStudio.imageSizes.saving'
              : 'avatarStudio.imageSizes.save',
          })}
        </Button>
      </Stack>
    </Stack>
  )
}
